/**
 * Authentication Service
 * Handles authentication-related API calls
 */

import axios from 'axios'
import { API_BASE_URL } from '../config/app.config'
import apiService from './api'
import { API_ENDPOINTS } from '../constants/endpoints'
import { StoredUserData, setJwtToken, setUserData } from '../utils/localStorage'
import { logoutAndRedirectToLogin } from '../utils/authSession'

/**
 * Login credentials
 */
export interface LoginCredentials {
	identifier: string
	password: string
}

/**
 * Login response from API
 */
export interface LoginResponse {
	jwt: string
	user: StoredUserData
}

/**
 * Auth Service Class
 */
class AuthService {
	/**
	 * Login user with credentials
	 * Note: Auth endpoint returns data directly, not in Strapi format
	 */
	async login(credentials: LoginCredentials): Promise<LoginResponse> {
		// Use axios directly since auth endpoint doesn't follow Strapi conventions
		const response = await axios.post<LoginResponse>(`${API_BASE_URL}/${API_ENDPOINTS.AUTH.LOGIN}`, credentials, {
			headers: {
				'Content-Type': 'application/json',
			},
		})

		const loginData = response.data

		if (loginData && loginData.jwt && loginData.user) {
			setJwtToken(loginData.jwt)
			setUserData(loginData.user)
			return loginData
		}

		throw new Error('Invalid login response')
	}

	/**
	 * Logout user
	 */
	logout(): void {
		logoutAndRedirectToLogin()
	}

	/**
	 * Get current user data
	 */
	async getUser(): Promise<StoredUserData> {
		const response = await apiService.get<StoredUserData>(`${API_ENDPOINTS.AUTH.ME}?populate=*`)
		return response.data as unknown as StoredUserData
	}

	/**
	 * Get user role
	 */
	async getRole(): Promise<string> {
		const user = await this.getUser()
		return user.role.type
	}
}

// Export singleton instance
const authService = new AuthService()
export default authService
